import type { WatchPart } from '../api/client'

export function formatLabel(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function formatPrice(price: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: price % 1 === 0 ? 0 : 2,
  }).format(price)
}

type DisplaySlot = { role?: string; position?: number | null }

function formatSlot(slot: DisplaySlot): string {
  const role = slot.role ? formatLabel(slot.role) : 'Unknown'
  if (slot.position != null) return `${role} @ ${slot.position}H`
  return role
}

export function complicationSummaryFor(part: WatchPart): string | null {
  const metadata = part.metadata ?? {}
  const dialLayout = metadata.dial_layout as
    | { openings?: Array<{ role?: string; position?: number | null }> }
    | undefined
  const movementCapabilities = metadata.movement_capabilities as
    | { displays?: Array<{ role?: string; position?: number | null }> }
    | undefined

  if (dialLayout?.openings) {
    if (dialLayout.openings.length === 0) return 'No date'
    return dialLayout.openings.map(formatSlot).join(' · ')
  }
  if (movementCapabilities?.displays) {
    if (movementCapabilities.displays.length === 0) return 'Time only'
    return movementCapabilities.displays.map(formatSlot).join(' · ')
  }
  return null
}

export function compactSpecs(part: WatchPart): Array<[string, string]> {
  const specs: Array<[string, string]> = []
  const { compatibility } = part

  if (part.material) specs.push(['Material', formatLabel(part.material)])
  if (compatibility.case_diameter) specs.push(['Case', `${compatibility.case_diameter} mm`])
  if (compatibility.lug_width) specs.push(['Lug', `${compatibility.lug_width} mm`])
  if (compatibility.dial_diameter) specs.push(['Dial', `${compatibility.dial_diameter} mm`])
  if (compatibility.water_resistance) {
    specs.push(['Water', `${compatibility.water_resistance} m`])
  }
  if (compatibility.movement_id) specs.push(['Movement', compatibility.movement_id])
  if (compatibility.compatible_movement_ids.length > 0) {
    specs.push(['Movements', compatibility.compatible_movement_ids.join(', ')])
  }
  if (compatibility.hand_hole_sizes) {
    specs.push([
      'Hand holes',
      Object.entries(compatibility.hand_hole_sizes)
        .map(([hand, size]) => `${hand} ${size}`)
        .join(' · '),
    ])
  }

  const complicationSummary = complicationSummaryFor(part)
  if (complicationSummary) specs.push(['Complication', complicationSummary])

  return specs.slice(0, 4)
}

export function compatibilityChips(part: WatchPart, max = 3): string[] {
  const chips: string[] = []
  const { compatibility } = part

  if (compatibility.case_diameter) chips.push(`${compatibility.case_diameter} mm case`)
  if (compatibility.lug_width) chips.push(`${compatibility.lug_width} mm lug`)
  if (compatibility.dial_diameter) chips.push(`${compatibility.dial_diameter} mm dial`)
  if (compatibility.water_resistance) chips.push(`${compatibility.water_resistance}m WR`)

  const complication = complicationSummaryFor(part)
  if (complication) chips.push(complication)

  if (compatibility.movement_id) chips.push(compatibility.movement_id)
  if (compatibility.compatible_movement_ids.length > 0) {
    chips.push(`${compatibility.compatible_movement_ids.length} movements`)
  }

  return chips.slice(0, max)
}
